import React, { useCallback, useEffect, useRef, useState } from 'react'
import { translationMemory, TMEntry } from '../lib/cat/translationMemory'

const PAGE_SIZE = 50

export default function TranslationMemoryPage() {
  const [entries, setEntries] = useState<TMEntry[]>([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState('')
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [clearConfirm, setClearConfirm] = useState(false)
  const [importLang, setImportLang] = useState({ src: 'en', tgt: 'pt' })
  const fileRef = useRef<HTMLInputElement>(null)
  const searchDebounce = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    clearTimeout(searchDebounce.current)
    searchDebounce.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(0)
    }, 400)
    return () => clearTimeout(searchDebounce.current)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    const result = await translationMemory.getAllEntries(page, PAGE_SIZE, debouncedSearch)
    setEntries(result.entries)
    setCount(result.count)
    setLoading(false)
  }, [page, debouncedSearch])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id: string) => {
    await translationMemory.deleteEntry(id)
    setDeleteConfirm(null)
    load()
  }

  const handleClearAll = async () => {
    await translationMemory.deleteAllEntries()
    setClearConfirm(false)
    setPage(0)
    load()
  }

  const handleExport = async () => {
    const all = await translationMemory.getAllEntriesForExport()
    if (!all.length) return
    const srcLang = all[0].source_language
    const tgtLang = all[0].target_language
    const xml = translationMemory.exportTMX(all, srcLang, tgtLang)
    const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `TranslyxTM_${new Date().toISOString().slice(0, 10)}.tmx`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)
    setImportMsg('')
    setImportProgress(null)
    try {
      const text = await file.text()
      const imported = await translationMemory.importTMX(
        text,
        importLang.src,
        importLang.tgt,
        (current, total) => setImportProgress({ current, total })
      )
      setImportProgress(null)
      setImportMsg(`✅ Imported ${imported} entries from ${file.name}`)
      load()
    } catch {
      setImportMsg('❌ Import failed — check file format')
    } finally {
      setImporting(false)
      setImportProgress(null)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const totalPages = Math.ceil(count / PAGE_SIZE)

  return (
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Translation Memory</h1>
            <p className="text-gray-700 text-sm mt-1">
              {count.toLocaleString()} stored translation{count !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={count === 0}
              className="px-4 py-2 text-sm rounded-lg bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              ↓ Export TMX
            </button>
            <button
              onClick={() => setClearConfirm(true)}
              disabled={count === 0}
              className="px-4 py-2 text-sm rounded-lg bg-red-100 hover:bg-red-200 text-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Import panel */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Import TMX File</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-gray-700 mb-1">Source language</label>
              <input
                type="text"
                value={importLang.src}
                onChange={e => setImportLang(p => ({ ...p, src: e.target.value }))}
                placeholder="en"
                className="w-20 px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-gold-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-700 mb-1">Target language</label>
              <input
                type="text"
                value={importLang.tgt}
                onChange={e => setImportLang(p => ({ ...p, tgt: e.target.value }))}
                placeholder="pt"
                className="w-20 px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-gold-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-700 mb-1">TMX file</label>
              <input
                ref={fileRef}
                type="file"
                accept=".tmx,.xml"
                onChange={handleImportFile}
                disabled={importing}
                className="block text-sm text-gray-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-gold-600 file:text-white file:text-sm file:cursor-pointer hover:file:bg-gold-500"
              />
            </div>
            {importing && (
              <div className="flex flex-col gap-1 min-w-[180px]">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <div className="animate-spin w-4 h-4 border-2 border-gold-500 border-t-transparent rounded-full" />
                  {importProgress
                    ? `Importing… ${importProgress.current} / ${importProgress.total}`
                    : 'Parsing file…'}
                </div>
                {importProgress && (
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-gold-500 h-1.5 rounded-full transition-all duration-200"
                      style={{ width: `${Math.round((importProgress.current / importProgress.total) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}
            {importMsg && <span className="text-sm text-gray-700">{importMsg}</span>}
          </div>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search source or target text…"
            className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gold-500 text-sm"
          />
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center h-32 gap-3 text-gray-700">
              <div className="animate-spin w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full" />
              Loading entries…
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center gap-2">
              <span className="text-3xl">📚</span>
              <span className="text-gray-700 text-sm">
                {debouncedSearch
                  ? 'No entries match your search'
                  : 'Your TM is empty. Confirm segments in the editor to populate it.'}
              </span>
            </div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-8">#</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Source</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider">Target</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">Langs</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-16">Used</th>
                    <th className="w-12 px-4" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry, idx) => (
                    <tr key={entry.id} className="hover:bg-gray-50 transition-colors group">
                      <td className="px-4 py-3 text-gray-800 text-xs">{page * PAGE_SIZE + idx + 1}</td>
                      <td className="px-4 py-3 text-gray-800 text-xs max-w-xs">
                        <div className="line-clamp-2">{entry.source_text}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-900 text-xs max-w-xs">
                        <div className="line-clamp-2">{entry.target_text}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {entry.source_language} → {entry.target_language}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-xs">{entry.usage_count}×</td>
                      <td className="px-4 py-3">
                        {deleteConfirm === entry.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleDelete(entry.id)} className="text-xs text-red-600 hover:text-red-700 font-medium">Yes</button>
                            <span className="text-gray-700">/</span>
                            <button onClick={() => setDeleteConfirm(null)} className="text-xs text-gray-700 hover:text-gray-700">No</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(entry.id)}
                            className="text-gray-700 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all text-lg leading-none"
                          >×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <span className="text-xs text-gray-700">
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, count)} of {count.toLocaleString()}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                      className="px-3 py-1.5 text-xs rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-40 hover:bg-gray-50 transition-colors">
                      ← Prev
                    </button>
                    <span className="text-xs text-gray-700">{page + 1} / {totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                      className="px-3 py-1.5 text-xs rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-40 hover:bg-gray-50 transition-colors">
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Clear All confirm modal */}
        {clearConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Clear all TM entries?</h3>
              <p className="text-gray-700 text-sm mb-6">
                This will permanently delete all {count.toLocaleString()} entries. Export a TMX backup first if needed.
              </p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setClearConfirm(false)}
                  className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
                  Cancel
                </button>
                <button onClick={handleClearAll}
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors">
                  Delete All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  )
}
